import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog,
  TextField,
  InputLabel,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InitialAddEditWoodChipFormData } from '../Data/WoodChipFormDataFields';
import moment from 'moment';
import useGetUser from 'routes/Auth/useGetUser';

// ─── Previous Samples Accordion ───────────────────────────────────────────────

const PreviousSamplesAccordion = ({ samples, loadId }) => {
  if (!samples?.length) return null;
  return (
    <Accordion disableGutters elevation={0} sx={{ backgroundColor: '#EFEFEF', mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Previous Samples for Load #{loadId} ({samples.length} found)
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {['Sample ID', 'Test #', 'Finish Weight', 'MC %', 'Starting Weight', 'Cook Time (hrs)', 'Finish Date'].map(
                  h => (
                    <TableCell key={h} sx={{ fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {samples.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.id}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.testNo ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.finishWeight ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.mC_Percent ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.startingWeight ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{s.cookTimeHours ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    {s.finishDate ? moment(s.finishDate).format('DD/MM/YY, HH:mm') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── FormField ────────────────────────────────────────────────────────────────

const FormField = ({ label, required, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <InputLabel sx={{ fontSize: '0.825rem', color: 'black', lineHeight: 1.2 }}>
      {label}
      {required && <span style={{ color: 'red' }}> *</span>}
    </InputLabel>
    {children}
  </Box>
);

// ─── MC % formula ─────────────────────────────────────────────────────────────

const calcMcPercent = (startingWeight, finishWeight) => {
  const sw = parseFloat(startingWeight);
  const fw = parseFloat(finishWeight);
  if (!sw || !fw || sw === 0) return '';
  return (((sw - fw) / sw) * 100).toFixed(2);
};

// ─── Field config ─────────────────────────────────────────────────────────────

const FIELD_CONFIG = [
  { name: 'wH_DocketNumber', label: 'Load ID (WH Docket #)', type: 'text', readOnly: true },
  { name: 'customer', label: 'Customer', type: 'text', readOnly: true },
  { name: 'sP_Number', label: 'SP Number', type: 'text', readOnly: true },
  { name: 'sampleHandler', label: 'Sample Handler', type: 'text', readOnly: true },
  { name: 'startingWeight', label: 'Starting Weight (kg)', type: 'number', readOnly: false, required: true },
  { name: 'finishWeight', label: 'Finish Weight (kg)', type: 'number', readOnly: false, required: true },
  { name: 'cookTimeHours', label: 'Cook Time (Hours)', type: 'number', readOnly: false, required: true },
  { name: 'finishDate', label: 'Finish Date', type: 'datetime-local', readOnly: false, required: true },
];

// ─── Modal ────────────────────────────────────────────────────────────────────

const AddEditWoodchipModal = ({
  open,
  setOpen,
  selectedWoodchipSample,
  addHandleWoodChipSampleDataSubmit,
  woodchipSampleData,
  isRetestMode,
}) => {
  const [formData, setFormData] = useState(InitialAddEditWoodChipFormData);
  const [previousSamples, setPreviousSamples] = useState([]);
  const [nextTestNo, setNextTestNo] = useState(1);

  const { user } = useGetUser();
  console.log(user?.displayName, user?.defaultDriverDisplayName);

  // Pending → always editing an existing record
  // Retest  → creating a new record (Add), identity fields cloned from the Retest row
  const isEdit = !isRetestMode && !!(open && selectedWoodchipSample?.id);

  useEffect(() => {
    if (!open) return;

    if (selectedWoodchipSample?.id) {
      setFormData({
        ...selectedWoodchipSample,
        startingWeight: selectedWoodchipSample.startingWeight ?? 250,
        finishDate: moment().format('YYYY-MM-DDTHH:mm'), // always default to now for fresh entry
        // clear lab result fields for retest so user fills them fresh
        finishWeight: isRetestMode ? '' : selectedWoodchipSample.finishWeight ?? '',
        cookTimeHours: isRetestMode ? '' : selectedWoodchipSample.cookTimeHours ?? '',
        mC_Percent: isRetestMode ? '' : selectedWoodchipSample.mC_Percent ?? '',
        sampleHandler: user?.displayName || user?.defaultDriverDisplayName || selectedWoodchipSample.sampleHandler || '',
      });

      // All completed samples for this docket — used in accordion + testNo calc
      const allForDocket = (woodchipSampleData || []).filter(
        s => String(s.wH_DocketNumber) === String(selectedWoodchipSample.wH_DocketNumber),
      );

      // Previous = completed records (exclude current row for Pending edit)
      const previous = allForDocket
        .filter(s => s.wH_DocketNumber === selectedWoodchipSample.wH_DocketNumber && s.mC_Percent !== null)
        .sort((a, b) => new Date(b.finishDate) - new Date(a.finishDate));
      setPreviousSamples(previous);

      // Next testNo = highest existing testNo across ALL records for this docket + 1
      const highestTestNo = allForDocket.reduce((max, s) => Math.max(max, s.testNo ?? 1), 0);
      setNextTestNo(highestTestNo);
    } else {
      setFormData(InitialAddEditWoodChipFormData);
      setPreviousSamples([]);
      setNextTestNo(1);
    }
  }, [open, selectedWoodchipSample, isRetestMode]);

  const autoMcPercent = useMemo(() => calcMcPercent(formData.startingWeight, formData.finishWeight), [
    formData.startingWeight,
    formData.finishWeight,
  ]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, mC_Percent: autoMcPercent }));
  }, [autoMcPercent]);

  const handleClose = () => setOpen(false);
  const handleFieldChange = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleFormSubmit = e => {
    e.preventDefault();
    addHandleWoodChipSampleDataSubmit(isEdit, {
      ...formData,
      mC_Percent: autoMcPercent,
      testNo: nextTestNo,
    });
  };

  const mcValue = parseFloat(autoMcPercent);
  const showMcAlert = autoMcPercent !== '' && (mcValue > 57 || mcValue < 40);

  // ─── Dialog title ──────────────────────────────────────────────────────────
  const dialogTitle = isRetestMode
    ? `Retest Sample — Load #${selectedWoodchipSample?.wH_DocketNumber} (Test #${nextTestNo})`
    : `Start Sampling — Load #${selectedWoodchipSample?.wH_DocketNumber} (Sample ID: ${selectedWoodchipSample?.id})`;

  return (
    <Dialog fullWidth maxWidth="md" onClose={handleClose} aria-labelledby="woodchip-dialog-title" open={open}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="woodchip-dialog-title">
        {dialogTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={theme => ({ position: 'absolute', right: 8, top: 8, color: theme.palette.grey[500] })}>
        <CloseIcon />
      </IconButton>

      {isRetestMode && (
        <Box sx={{ mx: 2, mt: 1 }}>
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            This is a retest. A new sample record (Test #{nextTestNo}) will be created for this load.
          </Alert>
        </Box>
      )}

      {showMcAlert && (
        <Box sx={{ mx: 2, mt: 1 }}>
          <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
            MC% is outside the recommended range (40%–57%). Please verify the weights before submitting.
          </Alert>
        </Box>
      )}

      <form
        onSubmit={handleFormSubmit}
        onKeyDown={e => {
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault();
        }}>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              columnGap: '16px',
              rowGap: '16px',
              '& .MuiInputBase-root': { fontSize: '0.825rem' },
            }}>
            {FIELD_CONFIG.map(field => (
              <FormField key={field.name} label={field.label} required={field.required}>
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  type={field.type}
                  value={formData[field.name] ?? ''}
                  onChange={e => handleFieldChange(field.name, e.target.value)}
                  required={field.required}
                  disabled={field.readOnly}
                  InputLabelProps={field.type === 'datetime-local' ? { shrink: true } : undefined}
                  inputProps={field.type === 'number' ? { min: 0, step: 'any' } : undefined}
                />
              </FormField>
            ))}

            {/* MC % — spans full row */}
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FormField label="MC % (auto-calculated)">
                <TextField
                  size="small"
                  fullWidth
                  value={autoMcPercent !== '' ? `${autoMcPercent}%` : '—'}
                  disabled
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: showMcAlert ? '#fff3e0' : autoMcPercent !== '' ? '#f1f8e9' : undefined,
                      fontWeight: 600,
                    },
                  }}
                />
                {autoMcPercent !== '' && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: '2px' }}>
                    = ((Starting Wt − Finish Wt) / Starting Wt) × 100
                  </Typography>
                )}
              </FormField>
            </Box>
          </Box>

          {/* Previous samples always shown for both Pending and Retest */}
          <PreviousSamplesAccordion samples={previousSamples} loadId={formData.wH_DocketNumber} />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            {isRetestMode ? `Submit Retest (Test #${nextTestNo})` : 'Save & Complete Sample'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditWoodchipModal;
